import { Grid, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import { useStationRouteContext } from "Helpers/stationRoutes";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Station } from "assets/SideBarIcons/station.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState } from "react";
import { GetTermsResponse } from "subModule/src/services/station/interface";
import Loader from "layout/Loader";
import { StationProfileService } from "subModule/src/core/services";
import StationTabs from "./StationTabs";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import { useFrozenStationTabBreadcrumbs } from "Helpers/useFrozenStationBreadcrumbs";
interface FormData {
  [key: string]: any;
}
export default function StationTermsAndConditions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { stationId, listPath } = useStationRouteContext();
  const breadcrumbItems = useFrozenStationTabBreadcrumbs("termsAndConditions");
  const { enqueueSnackbar } = useSnackbar();
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    reset,
  } = useForm<FormData>({ defaultValues: {} });
  const [loading, setLoading] = useState<boolean>(true);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(true);
  useEffect(() => {
    if (loading) getByID();
  }, []);
  useEffect(() => {
    if (isDirty && stationId) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields, stationId]);
  const getByID = () => {
    StationProfileService.getTermsByStationId(stationId!)
      .then((res: GetTermsResponse) => {
        if (res.success) {
          reset({ ...(res?.data || {}) });
          setLoading(false);
        } else {
          setLoading(false);
          if (res?.data)
            enqueueSnackbar(
              <AlertMessages text={res.message || "Something went wrong"} />,
              { variant: "error" }
            );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    delete data?.id;
    StationProfileService.createTerms({
      ...data,
      stationId: stationId!,
    })
      .then((response: GetTermsResponse) => {
        if (response.success) {
          getByID();
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={t("updated") + " " + t("successfully")}
            />,
            { variant: "success" }
          );
        } else {
          setIsBtnEnabled(true);
          enqueueSnackbar(<AlertMessages text={response?.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setIsBtnEnabled(true);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" }
        );
      });
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={checkPrivileges("ROLE_TERMANDCONDITION_CREATE")}
        onClick={() => navigate(listPath)}
        onSubmit={handleSubmit(onSubmit)}
        heading={t("stations")}
        icon={<Station height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
      />
      <div className="flex">
        <StationTabs params={params} selectedIndex={3} />
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={t("stations")}
            isButtonVisable={checkPrivileges("ROLE_TERMANDCONDITION_CREATE")}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={"update"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <>
                <Grid
                  container
                  component="form"
                  id="form"
                  spacing={2}
                  onSubmit={handleSubmit(onSubmit)}
                  sx={{ "& .MuiGrid-item": { paddingLeft: 0 } }}
                >
                  <Grid item xs={12}>
                    <TextField
                      {...register("estimation")}
                      label={t("estimation") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      placeholder={t("estimation") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      title={t("estimation") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      fullWidth
                      error={!!errors.estimation}
                      helperText={errors.estimation?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("secondaryEstimation")}
                      label={t("estimation") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      placeholder={t("estimation") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      title={t("estimation") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      fullWidth
                      error={!!errors.secondaryEstimation}
                      helperText={errors.secondaryEstimation?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("jobCard")}
                      label={t("jobCard") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      placeholder={t("jobCard") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      title={t("jobCard") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      fullWidth
                      error={!!errors.jobCard}
                      helperText={errors.jobCard?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("secondaryJobCard")}
                      label={t("jobCard") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      placeholder={t("jobCard") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      title={t("jobCard") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      fullWidth
                      error={!!errors.secondaryJobCard}
                      helperText={errors.secondaryJobCard?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("workOrder")}
                      label={t("workOrder") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      placeholder={t("workOrder") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      title={t("workOrder") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      fullWidth
                      error={!!errors.workOrder}
                      helperText={errors.workOrder?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("secondaryWorkOrder")}
                      label={t("workOrder") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      placeholder={t("workOrder") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      title={t("workOrder") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      fullWidth
                      error={!!errors.secondaryWorkOrder}
                      helperText={errors.secondaryWorkOrder?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("invoice")}
                      label={t("invoice") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      placeholder={t("invoice") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      title={t("invoice") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      fullWidth
                      error={!!errors.invoice}
                      helperText={errors.invoice?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("secondaryInvoice")}
                      label={t("invoice") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      placeholder={t("invoice") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      title={t("invoice") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      fullWidth
                      error={!!errors.secondaryInvoice}
                      helperText={errors.secondaryInvoice?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("gatePass")}
                      label={t("gatePass") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      placeholder={t("gatePass") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      title={t("gatePass") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      fullWidth
                      error={!!errors.gatePass}
                      helperText={errors.gatePass?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("secondaryGatePass")}
                      label={t("gatePass") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      placeholder={t("gatePass") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      title={t("gatePass") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      fullWidth
                      error={!!errors.secondaryGatePass}
                      helperText={errors.secondaryGatePass?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("inspection")}
                      label={t("inspection") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      placeholder={t("inspection") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      title={t("inspection") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      fullWidth
                      error={!!errors.inspection}
                      helperText={errors.inspection?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "block",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("secondaryInspection")}
                      label={t("inspection") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      placeholder={t("inspection") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      title={t("inspection") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      fullWidth
                      error={!!errors.secondaryInspection}
                      helperText={errors.secondaryInspection?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "block",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("cart")}
                      label={t("cart") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      placeholder={t("cart") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      title={t("cart") + " (" + t(`${primaryLanguage ? `${primaryLanguage}` : 'primary'}`) + ")"}
                      fullWidth
                      error={!!errors.cart}
                      helperText={errors.cart?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "block",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...register("secondaryCart")}
                      label={t("cart") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      placeholder={t("cart") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      title={t("cart") + " (" + t(`${secondaryLanguage ? `${secondaryLanguage}` : 'secondary'}`) + ")"}
                      fullWidth
                      error={!!errors.secondaryCart}
                      helperText={errors.secondaryCart?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={3}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "block",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            }
          />
        )}
      </div>
    </>
  );
}
